'use client';

/**
 * Shared auth profile store — single source of truth for the authenticated
 * user's profile data (name, email, avatar, onboarding state).
 *
 * WHY THIS EXISTS:
 * `useAuth()` is called from many components (UserMenu, settings, layouts).
 * Previously, profile lived in per-instance `useState` — each component had
 * its own isolated copy. Updating one didn't update the others.
 *
 * By storing profile in Zustand, ALL consumers share the same state.
 * When settings saves a name change, the UserMenu card updates instantly.
 *
 * WRITE PATHS:
 * - `setProfile(data)` — called by fetchProfile() on login/mount
 * - `patchProfile({ first_name, last_name })` — called after settings save
 * - `clearProfile()` — called on sign-out
 *
 * LOADING PATTERN:
 * Profile data is best-effort and loads in the background after auth is
 * confirmed. Components should read `profile` with optional chaining and
 * show a skeleton when it is null. The app shell gates on auth status only
 * (industry standard) — not on profile being loaded.
 */

import { create } from 'zustand';

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  workspace_id?: string;
  onboarding_completed: boolean;
  active_workspace_id?: string;
}

interface AuthProfileState {
  profile: UserProfile | null;

  /** Replace the entire profile (login / fetchProfile). */
  setProfile: (profile: UserProfile | null) => void;

  /** Merge partial updates (settings save). */
  patchProfile: (updates: Partial<UserProfile>) => void;

  /** Clear profile on sign-out. */
  clearProfile: () => void;
}

export const useAuthProfileStore = create<AuthProfileState>((set) => ({
  profile: null,

  setProfile: (profile) => set({ profile }),

  patchProfile: (updates) =>
    set((s) => (s.profile ? { profile: { ...s.profile, ...updates } } : s)),

  clearProfile: () => set({ profile: null }),
}));
